/**
 * ADMIN SUPABASE CLIENT - USE WITH EXTREME CAUTION
 * 
 * This client bypasses ALL Row Level Security (RLS) policies.
 * Only use for administrative operations that absolutely require
 * bypassing security policies.
 * 
 * Consider using the regular authenticated clients first:
 * - createServerClient() for server components
 * - createBrowserClient() for client components
 * - createRouteHandlerClient() for API routes
 * 
 * Usage of this client should be audited regularly.
 * Search for imports: grep -r "supabase-admin" --include="*.ts" --include="*.tsx"
 */

import { createClient } from '@supabase/supabase-js';
import { Database } from './database.types';
import { createLogger } from './utils/logger';
import type { SupabaseClient as SupabaseClientType } from '@supabase/supabase-js';

const logger = createLogger('supabase:admin');

// Export specific type for our database
export type SupabaseClient = SupabaseClientType<Database>;

/**
 * Creates an admin Supabase client that bypasses Row Level Security
 * 
 * WARNING: This client has full access to all data regardless of RLS policies.
 * Only use when absolutely necessary for administrative operations.
 * 
 * Common use cases:
 * - Background jobs that need to access data across multiple users
 * - System-level operations (cleanup, maintenance)
 * - Initial data seeding
 * 
 * DO NOT use for:
 * - Regular user operations
 * - Any operation that could be done with user context
 * - Client-side code (this should only be used server-side)
 */
export function createAdminSupabaseClient(): SupabaseClient {
  const supabaseUrl = process.env.SUPABASE_URL || process.env.NEXT_PUBLIC_SUPABASE_URL;
  const serviceRoleKey = process.env.SUPABASE_SERVICE_ROLE_KEY;
  
  if (!supabaseUrl) {
    throw new Error('Missing SUPABASE_URL or NEXT_PUBLIC_SUPABASE_URL environment variable');
  }
  
  if (!serviceRoleKey) {
    throw new Error(
      'Missing SUPABASE_SERVICE_ROLE_KEY environment variable. ' +
      'Admin operations require the service role key.'
    );
  }
  
  logger.info('Creating admin Supabase client');
  
  return createClient<Database>(
    supabaseUrl,
    serviceRoleKey,
    {
      auth: {
        autoRefreshToken: false,
        persistSession: false,
        detectSessionInUrl: false
      }
    }
  );
}